import React, { useEffect, useRef, useState } from 'react';
import '../../App.css';
import {
  required,
  SelectInput,
  TextInput,
  useDataProvider,
  DateTimeInput,
  AutocompleteInput,
  FormDataConsumer,
  useNotify,
  useRefresh,
  useTranslate,
  Notification,
} from 'react-admin';
import { eventStatus } from '../../utils/choices';
import { appStyles, columnStyles } from 'styles';
import { BaseForm } from '../../Components';
import { ActionButton } from '../../Components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import { EventPlotForm } from '../../../src/Components/Forms';
import { Dialog } from '@material-ui/core';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import CustomLocationDescForm from 'Components/Forms/CustomLocationDesc';

// Material-UI adds an extra div around AutoCompleteInput that sets a flex-grow: 1
// which pushing the component to the left. In order to disable need another wrapper to overwrite it
const useStyles = makeStyles({
  root: {
    flexGrow: 0,
  },
});

// Details and Update uses this form
const EventForm = (props) => {
  const form = columnStyles('column', '30vw');
  const classes = appStyles();
  const autoCompleteStyle = useStyles();
  const [plots, setPlots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locDescription, setLocDescription] = useState();
  const [admins, setAdmins] = useState([]);
  const [eventDate, setEventDate] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [plot, setPlot] = useState();
  const notify = useNotify();
  const refresh = useRefresh();

  const [showDialog, setShowDialog] = useState(false);

  const handleClick = () => {
    setShowDialog(true);
  };

  const handleCloseClick = () => {
    setShowDialog(false);
  };

  const translate = useTranslate();

  const dataProvider = useDataProvider();

  const handleCompleteEvent = () => {
    const { record } = props;
    if (record.status !== 'COMPLETED') {
      setLoading(true);
      props.record.status = 'COMPLETED';
      dataProvider('UPDATE', 'event', {
        data: record,
      })
        .then(({ data }) => {
          setLoading(false);
          refresh();
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
          notify(error.message, 'warning');
        }).finally(() => {
          setShowDialog(false);
        });
    }
  };

  const onPlotChange = (e) => {
    dataProvider
      .getOne('plot', { id: e })
      .then(({ data }) => {
        setPlot(data)
        // formData.change(`evidenceCode1`, data.evidenceCode1)
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleStatusChange = (e) => {
    const { record } = props;
    record.status = e.target.value
  }
  // const isCompletable = [
  //   'IN_PROGRESS',
  //   'PUBLISHED',
  // ].includes(props.record.status)

  useEffect(() => {
    dataProvider
      .getList('plot', {})
      .then(({ data }) => {
        const plotArray = data.map(({ id, name }) => ({ id, name }));
        setPlots(plotArray);
      })
      .catch((error) => {
        console.log(error);
      });
    dataProvider
      .getList('location', {})
      .then(({ data }) => {
        const locArray = data.map(({ id, name }) => ({ id, name }));
        const locDescArray = data.map(({ id, description }) => ({ id, description }));
        setLocations(locArray);
        setLocDescription(locDescArray);
      })
      .catch((error) => {
        console.log(error);
      });

    dataProvider
      .getList('admin', {})
      .then(({ data }) => {
        const adminArray = data.map(({ id, email, role }) => ({
          id,
          email,
          role,
        }));
        setAdmins(adminArray);
      })
      .catch((error) => {
        console.log(error);
      });
    // DateTimeInput does not wait for the record to arrive
    // and returns undefined if directly accessed to the record object
    if (props.record.eventDate) {
      setEventDate(
        `${props.record.eventDate.date}T${props.record.eventDate.time}`
      );
    }
    if (props.record.status == 'COMPLETED') {
      setIsCompleted(true);
    }
  }, []);

  const childRef = useRef();

  const handleChildMethod = (event) => {
    const locDescFind = locDescription.find((loc) => loc.id === event);

    childRef.current.changeLocationDescription(locDescFind);
  };

  return (
    <BaseForm
      iscompleted={isCompleted}
      {...props}
      dialogContent='Are you sure want to delete this item?This will remove all associated evidences, suspects and supersleuths'
    >
      <div className={form.container}>
        <TextInput
          className={form.column}
          source="name"
          label="Event Title"
          validate={required()}
          disabled={isCompleted}
        />

        <div className={autoCompleteStyle.root}>
          <AutocompleteInput
            className={form.column}
            source="eventLocation.id"
            label="Location"
            choices={locations}
            validate={required()}
            disabled={isCompleted}
            onChange={handleChildMethod}
          />
        </div>
        <FormDataConsumer>
          {(formDataProps) =>
            <CustomLocationDescForm ref={childRef} {...formDataProps} />
          }
        </FormDataConsumer>

        {
          props.record.plot ?
            <div className={autoCompleteStyle.root}>
              <AutocompleteInput
                className={form.column}
                source="plot.id"
                label="Plot"
                onChange={onPlotChange}
                choices={plots}
                validate={required()}
                disabled={true}
              />
            </div> :
            <div className={autoCompleteStyle.root}>
              <AutocompleteInput
                className={form.column}
                source="plot.id"
                label="Plot"
                onChange={onPlotChange}
                choices={plots}
                validate={required()}
                disabled={isCompleted}
              />
            </div>
        }
        <div className={autoCompleteStyle.root}>
          <AutocompleteInput
            className={form.column}
            source="eventDirector.id"
            choices={admins}
            optionText="email"
            optionValue="id"
            label="Event Director"
            validate={required()}
            disabled={isCompleted}
          />
        </div>
        <FormDataConsumer>
          {
            formDataProps => (
              <EventPlotForm  {...formDataProps} plot={plot} iscompleted={isCompleted} />
            )
          }
        </FormDataConsumer>
        <DateTimeInput
          className={form.column}
          source="eventDateISO"
          label="Date / Time"
          disabled={isCompleted}
          parse={(dat) => {
            return moment.utc(dat).format('YYYY-MM-DDTHH:mm');
          }}
          format={(dat) => {
            if (dat) {
              return moment.utc(dat).utc().format('YYYY-MM-DDTHH:mm');
            }
          }}
        />
        <SelectInput
          className={form.column}
          source="status"
          choices={eventStatus}
          validate={required()}
          onChange={handleStatusChange}
          disabled={isCompleted}
        />
        <TextInput
          className={form.column}
          source="eventCode"
          validate={required()}
          disabled={isCompleted}
        />
      </div>
      {!isCompleted ? (
        <ActionButton
          label="Complete Event"
          onClick={handleClick}
          style={{ backgroundColor: 'green' }}
          icon={<FontAwesomeIcon icon={faCheckCircle} />}
        />
      ) : null}

      {loading ? (
        <CircularProgress size={20} className={classes.circularProgress} />
      ) : null}

      <Dialog
        fullWidth
        open={showDialog}
        onClose={handleCloseClick}
        aria-label={`Edit`}
      >
        <div style={{ padding: '1rem' }}>
          Are you sure you want to complete this event? If you complete an event, users will not be able to send their suspects anymore.
        </div>

        <div>
          <ActionButton
            label="Cancel"
            onClick={handleCloseClick}
            style={{ backgroundColor: 'gray', margin: '1rem', float: 'left' }}
            icon={<FontAwesomeIcon icon={faTimes} />}
          />
          <ActionButton
            label="Complete"
            onClick={handleCompleteEvent}
            style={{ backgroundColor: 'green', margin: '1rem', float: 'right' }}
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
          />

        </div>

        <Notification />
      </Dialog>
    </BaseForm>
  );
};

export default EventForm;
